import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Lock, ShieldCheck, Loader, AlertCircle } from "lucide-react";
import { useBetaGate } from "../context/BetaGateContext";
import { formatApiErrorDetail } from "../lib/api";
import ServerStatus from "../components/ServerStatus";

/**
 * BetaGate — the passphrase screen that blocks the entire app for non-invited
 * visitors. Renders edge-to-edge over everything else. NO product info on
 * this screen, so a leaked URL alone reveals nothing about the product.
 *
 * After a successful unlock, the provider transitions to a "celebrating"
 * state (handled by GatedApp via <BetaWelcome />) before the app routes
 * through. This component itself just handles the form.
 *
 * Query-string auto-fill: `?email=...&code=...` pre-populates the form (used
 * by the admin "Test as a tester" sandbox flow). Auto-submit is intentionally
 * NOT triggered — the admin should review before unlocking.
 */
export default function BetaGate() {
  const { unlock, unlocked, celebrating } = useBetaGate();
  const location = useLocation();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // One-time read of ?email=&code= for the admin sandbox / shareable preview
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const e = (params.get("email") || "").trim();
    const c = (params.get("code") || "").trim().toUpperCase();
    if (e) setEmail(e);
    if (c) setCode(c);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Warm-up ping + live status is handled by <ServerStatus /> below — it
  // wakes the sandbox backend the moment the gate renders and shows testers
  // exactly when it's ready.

  // Redirect when unlock finishes — this is required when the gate page is
  // reached via the email link (`/beta?email=...`). Without it, the user
  // stays staring at the gate after the celebration animation ends. We send
  // them straight to /register because the celebration screen already
  // congratulated them and the invite email already pitched the product —
  // no need to detour through the marketing welcome page.
  useEffect(() => {
    if (unlocked && !celebrating) {
      navigate("/register", { replace: true });
    }
  }, [unlocked, celebrating, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!code.trim() || !email.trim()) {
      setError("Both your invite code and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      await unlock(code, email);
      // Provider flips to celebrating → unlocked; GatedApp swaps us out.
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(formatApiErrorDetail(detail) || "Unable to validate. Check your invite and try again.");
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{
        background: "#0F1B2D",
        backgroundImage: "radial-gradient(circle at 10% 10%, rgba(255,106,0,0.08), transparent 40%), radial-gradient(circle at 90% 90%, rgba(255,193,69,0.06), transparent 40%)",
      }}
      data-testid="beta-gate-screen"
    >
      <div className="w-full max-w-md relative">
        {/* Brand wordmark — Resmo perched BEHIND the wordmark, holding it up.
            His head is intentionally tucked behind the bold "RESMO." text so
            only his arms + thumbs up + suit show — no eye to worry about. */}
        <div className="relative text-center mb-8">
          {/* Resmo — Desktop: smaller, centered behind the wordmark */}
          <img
            src="/brand/resmo-character.png"
            alt=""
            className="hidden md:block pointer-events-none select-none absolute"
            aria-hidden="true"
            style={{
              top: "-78px",
              left: "calc(50% + 60px)",
              width: "100px",
              height: "auto",
              transform: "translateX(-50%) rotate(8deg)",
              filter: "drop-shadow(0 6px 16px rgba(0,0,0,0.55))",
              zIndex: 0,
            }}
            data-testid="beta-gate-mascot"
            draggable={false}
          />

          <div
            className="font-display uppercase leading-none mb-2 relative"
            style={{
              fontSize: "3.5rem",
              letterSpacing: "-0.04em",
              color: "white",
              zIndex: 1,
              textShadow: "0 2px 14px rgba(15,27,45,0.85), 0 0 28px rgba(15,27,45,0.7)",
            }}
          >
            <span
              style={{
                background: "linear-gradient(135deg, #FFD089 0%, #FFA64D 45%, #FF7A00 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
                color: "transparent",
                filter: "drop-shadow(0 0 18px rgba(255, 138, 51, 0.5))",
              }}
            >
              R
            </span>esmo<span style={{ color: "#FFC145" }}>.</span>
          </div>
          <div
            className="font-mono-rm text-xs uppercase tracking-widest relative"
            style={{ color: "#94A3B8", zIndex: 1 }}
          >
            Private beta · Invitation required
          </div>
          <div
            className="mt-2 font-display text-sm md:text-base relative"
            style={{ color: "#FFC145", letterSpacing: "0.02em", zIndex: 1 }}
            data-testid="beta-gate-tagline"
          >
            One resume. <span className="rm-text-orange">Endless opportunities.</span>
          </div>
        </div>

        {/* Resmo — Mobile/tablet: small, centered above the card */}
        <img
          src="/brand/resmo-character.png"
          alt=""
          className="md:hidden mx-auto block pointer-events-none select-none mb-4"
          aria-hidden="true"
          style={{
            width: "100px",
            height: "auto",
            filter: "drop-shadow(0 8px 20px rgba(0,0,0,0.5))",
          }}
          data-testid="beta-gate-mascot-mobile"
          draggable={false}
        />

        <div
          className="p-7 md:p-8 relative"
          style={{
            background: "white",
            border: "3px solid #FFC145",
            borderRadius: 12,
            boxShadow: "8px 8px 0 0 rgba(255,193,69,0.35)",
            zIndex: 2,
          }}
        >
          <div className="flex items-center gap-3 mb-5">
            <div
              className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: "#0F1B2D", color: "#FFC145", border: "3px solid #0F1B2D" }}
            >
              <Lock size={20} strokeWidth={2.5} />
            </div>
            <div>
              <div className="rm-label">Beta Access</div>
              <h1 className="font-display text-xl uppercase leading-tight">
                Enter your <span className="rm-text-orange">invite</span>
              </h1>
            </div>
            <ServerStatus />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="rm-label block mb-1.5" htmlFor="beta-email">Your email</label>
              <input
                id="beta-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-3 py-3 font-mono-rm text-sm focus:outline-none"
                style={{
                  background: "#FFFBEB",
                  border: "3px solid #0F1B2D",
                  borderRadius: 6,
                }}
                data-testid="beta-gate-email-input"
                disabled={submitting}
              />
            </div>
            <div>
              <label className="rm-label block mb-1.5" htmlFor="beta-code">Invite code</label>
              <input
                id="beta-code"
                type="text"
                autoComplete="off"
                spellCheck={false}
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="RESMO-XXXXXXXX"
                className="w-full px-3 py-3 font-mono-rm text-base focus:outline-none tracking-wider"
                style={{
                  background: "#FFFBEB",
                  border: "3px solid #0F1B2D",
                  borderRadius: 6,
                  letterSpacing: "0.1em",
                }}
                data-testid="beta-gate-code-input"
                disabled={submitting}
              />
              <div className="font-mono-rm text-[11px] mt-1.5" style={{ color: "#475569" }}>
                The code we emailed to you. Format: RESMO-XXXXXXXX
              </div>
            </div>

            {error && (
              <div
                className="p-3 rounded-md font-mono-rm text-sm flex items-start gap-2"
                style={{ background: "#FEF2F2", border: "2px solid #EF4444", color: "#991B1B" }}
                data-testid="beta-gate-error"
              >
                <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="rm-btn rm-btn-primary w-full justify-center"
              data-testid="beta-gate-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader size={16} className="animate-spin" /> Validating…
                </>
              ) : (
                <>
                  <ShieldCheck size={16} /> Unlock
                </>
              )}
            </button>
          </form>

          <div className="mt-5 pt-4 border-t-2 border-dashed"
               style={{ borderColor: "#E2E8F0" }}>
            <WaitlistInline />
          </div>

          {/* Sandbox warm-up notice — set expectations before the first click */}
          <div className="mt-4 p-3 rounded-md flex items-start gap-2"
               style={{ background: "#FFF7ED", border: "1.5px dashed #F59E0B" }}
               data-testid="beta-gate-sandbox-note">
            <span style={{ fontSize: 14 }}>☕</span>
            <p className="font-mono-rm text-[11px] leading-relaxed m-0" style={{ color: "#92400E" }}>
              <b>Heads up:</b> this beta runs in a sandbox — after a quiet spell the
              servers take a minute to wake up. If your first click errors or hangs,
              nothing is broken: wait ~30 seconds and try again.
            </p>
          </div>
        </div>

        <div className="text-center mt-6 font-mono-rm text-[10px] uppercase tracking-widest"
             style={{ color: "#475569" }}>
          By unlocking, you agree to your signed NDA and the private beta terms.
          <div className="mt-1">
            <a href="/privacy" style={{ color: "#FF6A00", textDecoration: "underline" }} data-testid="beta-gate-privacy-link">Privacy</a>
            <span style={{ margin: "0 6px" }}>·</span>
            <a href="/terms" style={{ color: "#FF6A00", textDecoration: "underline" }} data-testid="beta-gate-terms-link">Terms</a>
          </div>
        </div>
      </div>
    </div>
  );
}

// Live backend status pill — shared component (also used on /login). Pinging
// doubles as the sandbox wake-up call.

function WaitlistInline() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !email.includes("@")) { setError("Need a valid email."); return; }
    setBusy(true);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") || "";
      const res = await fetch(`${apiBase}/api/waitlist/join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "beta_gate" }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || "Couldn't join the waitlist.");
        return;
      }
      setDone(data);
    } catch {
      setError("Network hiccup — try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="rm-card-static p-3 text-center"
           style={{ background: "#ECFDF5", borderColor: "#047857" }}
           data-testid="beta-gate-waitlist-success">
        <div className="font-mono-rm uppercase text-[10px] tracking-widest"
             style={{ color: "#064E3B", fontWeight: 700 }}>
          · You're on the list
        </div>
        <p className="text-sm mt-1" style={{ color: "#064E3B" }}>
          {done.already_on_list
            ? "Already on the list — we'll be in touch the moment a seat opens."
            : "Watch your inbox for an invite code."}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-2" data-testid="beta-gate-waitlist-form">
      <div className="font-mono-rm text-[11px] uppercase tracking-widest text-center mb-2"
           style={{ color: "#475569", fontWeight: 700 }}>
        · No invite yet? Join the waitlist.
      </div>
      <div className="flex gap-2">
        <input type="email"
               value={email}
               onChange={(e) => setEmail(e.target.value)}
               placeholder="you@example.com"
               className="flex-1 px-3 py-2 font-mono-rm text-[13px] focus:outline-none"
               style={{ background: "#FFFBEB", border: "2px solid #0F1B2D", borderRadius: 6 }}
               data-testid="beta-gate-waitlist-email"
               disabled={busy}
               required />
        <button type="submit"
                disabled={busy}
                className="rm-btn rm-btn-primary text-xs px-3 py-2"
                data-testid="beta-gate-waitlist-submit">
          {busy ? <Loader size={12} className="animate-spin" /> : "Join"}
        </button>
      </div>
      {error && (
        <div className="font-mono-rm text-[11px]" style={{ color: "#991B1B" }}
             data-testid="beta-gate-waitlist-error">
          · {error}
        </div>
      )}
    </form>
  );
}
